import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Input, InputNumber, Row, Select, theme } from 'antd';
import { UKPostCodeInput } from 'pos-client/components/generic/address/UKPostCodeInput.tsx';
import { StateSelect } from 'pos-client/components/generic/states/StateSelect.tsx';
import { PosFormItem } from 'pos-client/components/shared/Form/PosFormItem.tsx';
import { useCountryStaticConfigData } from 'pos-client/hooks/useStaticConfig.tsx';
import type { LocationCreateEditModel } from 'pos-shared/models/locationSchema.ts';

export const LocationAddressTab = ({
  locationForm,
  selectedCountry,
}: {
  locationForm: UseFormReturn<LocationCreateEditModel>;
  selectedCountry?: string;
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { countryOptions } = useCountryStaticConfigData();
  const UKUser = locationForm.watch('address.country') === 'United Kingdom';

  return (
    <>
      <Row gutter={[token.marginSM, 0]}>
        <Col span={12}>
          <PosFormItem
            name="address.addressLine1"
            control={locationForm.control}
            label={t('common.address.addressLine1')}
            required
          >
            <Input />
          </PosFormItem>
        </Col>
        <Col span={12}>
          <PosFormItem
            name="address.addressLine2"
            control={locationForm.control}
            label={t('common.address.addressLine2')}
          >
            <Input />
          </PosFormItem>
        </Col>
      </Row>
      <Row gutter={[token.marginSM, 0]}>
        <Col span={12}>
          <PosFormItem
            name="address.addressLine3"
            control={locationForm.control}
            label={t('common.address.addressLine3')}
          >
            <Input />
          </PosFormItem>
        </Col>
        <Col span={12}>
          <PosFormItem
            name="address.addressLine4"
            control={locationForm.control}
            label={t('common.address.city')}
            required
          >
            <Input />
          </PosFormItem>
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {selectedCountry === 'United States' ? (
            <StateSelect
              name="address.state"
              control={locationForm.control}
              required
            />
          ) : (
            <PosFormItem
              name="address.state"
              control={locationForm.control}
              label={t('common.address.state')}
            >
              <Input />
            </PosFormItem>
          )}
        </Col>
      </Row>

      <Row gutter={[token.marginSM, 0]}>
        <Col span={12}>
          <PosFormItem
            name="address.country"
            control={locationForm.control}
            label={t('common.address.country')}
            required
          >
            <Select
              showSearch
              placeholder={t('common.buttons.select')}
              options={countryOptions?.map((country) => {
                return {
                  value: country.name,
                  label: country.name,
                };
              })}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </PosFormItem>
        </Col>

        <Col span={12}>
          {UKUser ? (
            <UKPostCodeInput
              form={locationForm}
              fieldName={'address.postalCode'}
            />
          ) : (
            <PosFormItem
              name="address.postalCode"
              control={locationForm.control}
              label={t('common.address.zipCode')}
              formItemProps={{ style: { marginBottom: token.marginXS } }}
              required
            >
              <Input />
            </PosFormItem>
          )}
        </Col>
        <Col span={12}>
          <PosFormItem
            name="address.geoLocation.latitude"
            control={locationForm.control}
            label={t('common.address.latitude')}
            formItemProps={{ style: { marginBottom: token.marginXS } }}
            required
          >
            <InputNumber
              type="number"
              controls={false}
              style={{ width: '100%' }}
            />
          </PosFormItem>
        </Col>
        <Col span={12}>
          <PosFormItem
            name="address.geoLocation.longitude"
            control={locationForm.control}
            label={t('common.address.longitude')}
            formItemProps={{ style: { marginBottom: token.marginXS } }}
            required
          >
            <InputNumber
              type="number"
              controls={false}
              style={{ width: '100%' }}
            />
          </PosFormItem>
        </Col>
      </Row>
    </>
  );
};
